/* eslint-disable react/no-unescaped-entities */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Globe, Sparkles, SlidersHorizontal, BookMarked, Building2 } from 'lucide-react';
import { SitesTab } from '@/components/payzen/vitrine/SitesTab';
import { AutoTab } from '@/components/payzen/vitrine/AutoTab';
import { ManualTab } from '@/components/payzen/vitrine/ManualTab';
import { ProfilesTab } from '@/components/payzen/vitrine/ProfilesTab';

export default function PayzenVitrine() {
  const [params, setParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [accId, setAccId] = useState(params.get('account') || '');
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState(params.get('tab') || 'sites');

  useEffect(() => { api.get('/payzen-accounts').then((r) => { setAccounts(r.data); if (!accId && r.data[0]) setAccId(r.data[0].id); }).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const reload = useCallback(async () => { if (!accId) return; try { const r = await api.get(`/payzen-accounts/${accId}/vitrine`); setOverview(r.data); } catch { setOverview(null); } }, [accId]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { const p = {}; if (accId) p.account = accId; if (tab) p.tab = tab; setParams(p, { replace: true }); }, [accId, tab, setParams]);

  const eqs = overview?.equivalences || { total: 0, manual: 0, by_kind: {} };
  const kpis = [
    ['Sites vitrine', overview?.sites?.length ?? 0], ['Produits scrapés', overview?.catalog_count ?? 0], ['Boutiques reliées', overview?.stores?.length ?? 0],
    ['Équivalences', eqs.total], ['Manuelles', eqs.manual], ['Sans équivalent', eqs.by_kind?.generic || 0],
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl" data-testid="payzen-vitrine-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
        <div>
          <div className="overline mb-2">— Infrastructure · PayZen</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Vitrine PayZen</h1>
          <p className="text-zinc-600 mt-2 max-w-2xl">Scrapez vos sites vitrine, laissez le moteur trouver pour chaque produit l'équivalent au bon prix (seul, en multiple ou en combinaison), ajustez à la main, et seul ce descriptif est transmis à PayZen.</p>
        </div>
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-zinc-400" />
          <select value={accId} onChange={(e) => setAccId(e.target.value)} className="h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm min-w-[220px]" data-testid="pzv-account-select">
            {!accounts.length && <option value="">Aucun compte PayZen</option>}
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.environment === 'prod' ? 'LIVE' : 'TEST'}</option>)}
          </select>
        </div>
      </div>

      {!accId ? <Card className="card-flat p-10 text-center text-sm text-zinc-500" data-testid="pzv-empty">Ajoutez d'abord un compte PayZen dans « Comptes PayZen ».</Card> : (
        <>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-6" data-testid="pzv-kpis">
            {kpis.map(([l, v]) => <div key={l} className="rounded-md border border-zinc-200 bg-zinc-50 p-3"><div className="text-[10px] uppercase tracking-widest text-zinc-500">{l}</div><div className="font-mono text-lg font-semibold tabular-nums">{v}</div></div>)}
          </div>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-4 flex-wrap h-auto">
              <TabsTrigger value="sites" data-testid="pzv-tab-sites"><Globe className="h-3.5 w-3.5 mr-1.5" /> Sites & scraping</TabsTrigger>
              <TabsTrigger value="auto" data-testid="pzv-tab-auto"><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Moteur d'équivalence</TabsTrigger>
              <TabsTrigger value="manual" data-testid="pzv-tab-manual"><SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" /> Équivalences manuelles</TabsTrigger>
              <TabsTrigger value="profiles" data-testid="pzv-tab-profiles"><BookMarked className="h-3.5 w-3.5 mr-1.5" /> Modèles</TabsTrigger>
            </TabsList>
            <TabsContent value="sites"><SitesTab accId={accId} overview={overview} reload={reload} /></TabsContent>
            <TabsContent value="auto"><AutoTab accId={accId} overview={overview} reload={reload} /></TabsContent>
            <TabsContent value="manual"><ManualTab accId={accId} overview={overview} reload={reload} /></TabsContent>
            <TabsContent value="profiles"><ProfilesTab accId={accId} overview={overview} reload={reload} /></TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
